import { PopoverPlacement } from "@idui/react-popover";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  PhoneForwarded as PhoneForwardedIcon,
} from "@mui/icons-material";
import { IconButton, Popover } from "@mui/material";
import clsx from "clsx";
import { get } from "lodash";
import { useEffect, useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import Select, {
  type GroupBase,
  type OptionProps,
  type SingleValue,
} from "react-select";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useDropdownSourceContext, useRidesActions, useRtl } from "@ryde/redux";
import {
  callToClickHandler,
  dropdownSelectorFamily,
  errors,
  formatPhoneLocal,
  getIsDisabledByCondition,
  isNullOrEmptySpace,
  isRideUpdateDriverOrVehicleFieldName,
  Log,
  NO_CACHE_KEY,
  NO_VALUE_DISPLAY,
  profilePermissionsSelector,
  sortDropdownItemsByArray,
  useHasPermissionCustomField,
  warnAndToastSimpleResult,
  type IDropdownItem,
  type IRideDto,
  type RideUpdateDriverOrVehiclePatch,
} from "@ryde/shared";

import type { IRenderColumnTemplate } from "../_core";
import { FramerTooltip } from "../../../common";
import {
  RideEditableDropdownBoundTemplateStyled,
  RideEditableDropdownPopoverContainer,
  RideEditableDropdownPopoverFooter,
} from "./ride-editable-dropdown-bound.styled";

const getOptionClassName = (
  state: OptionProps<IDropdownItem, false, GroupBase<IDropdownItem>>,
): string =>
  clsx("react-select__option", {
    "react-select__option--focused": state.isFocused,
  });

export const RideEditableDropdownBoundTemplate: FC<IRenderColumnTemplate> = ({
  value,
  e2eData,
  formAttributes,
  gridAttributes,
  rowOriginal,
  fieldName,
  valueTranslated,
  readPermission,
  updatePermission,
}) => {
  const { t } = useTranslation();
  const roleDataItem = useRecoilValue(profilePermissionsSelector);
  const valueComputed = valueTranslated
    ? String(valueTranslated)
    : String(value);
  const dropdownItems = useRecoilValue(
    dropdownSelectorFamily(formAttributes?.dropItemsSource ?? NO_CACHE_KEY),
  );

  const [lastValue, setLastValue] = useState<number | null>();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const { getDropdownItems } = useDropdownSourceContext();
  const { updateRideDriverOrVehicle } = useRidesActions();
  const { getFieldPermissions } = useHasPermissionCustomField();
  const { isRtl } = useRtl();
  const isDisabledByCondition = getIsDisabledByCondition({
    fieldValues: rowOriginal,
    conditions: gridAttributes?.disableCondition,
    permissions: roleDataItem?.permissions,
  });

  const supplierId = get(rowOriginal, "supplierId") as IRideDto["supplierId"];
  const phoneCallableByFieldName = gridAttributes?.phoneCallableByFieldName;
  const phoneCallable = phoneCallableByFieldName
    ? get(rowOriginal, phoneCallableByFieldName)
    : undefined;

  const { isFieldVisible, isFieldDisabled } = useMemo(
    () =>
      getFieldPermissions({
        readPermission,
        updatePermission,
      }),
    [getFieldPermissions, readPermission, updatePermission],
  );

  const handleClick = useEvent(event => {
    setAnchorEl(event.currentTarget);
  });
  const handleClose = useEvent(() => {
    setAnchorEl(null);
  });

  useEffect(() => {
    if (!formAttributes?.dropItemsSource) {
      return;
    }

    getDropdownItems(formAttributes?.dropItemsSource);
  }, [formAttributes?.dropItemsSource, getDropdownItems]);

  const filteredDropdownItems = useMemo(() => {
    if (!supplierId) {
      return dropdownItems;
    }

    return dropdownItems.filter(x => x.supplierId === supplierId);
  }, [dropdownItems, supplierId]);

  const sortedDropdownItems = useMemo(() => {
    if (!lastValue) {
      return filteredDropdownItems;
    }

    return sortDropdownItemsByArray(filteredDropdownItems, [lastValue]);
  }, [filteredDropdownItems, lastValue]);

  const updateValue = useEvent(async (newValue: number | null) => {
    if (!fieldName || !isRideUpdateDriverOrVehicleFieldName(fieldName)) {
      Log.warnAndToast(t(errors.ride.errorUpdatingRideData));
      return;
    }

    const patchedFields: RideUpdateDriverOrVehiclePatch = {
      [fieldName]: newValue,
    };

    const updateResultPromise = updateRideDriverOrVehicle({
      rideId: (rowOriginal as IRideDto)?.id as number,
      isReturnDtoForGrid: true,
      patchedFields,
    });

    handleClose();

    try {
      const result = await updateResultPromise;

      if (!result.success) {
        warnAndToastSimpleResult(result, t);
      }
    } catch (error) {
      Log.warnAndToast(t(errors.ride.errorUpdatingRideData), error);
    }
  });

  const handleOnChange = useEvent((newValue: SingleValue<IDropdownItem>) => {
    setLastValue(null);
    updateValue(newValue?.value as number);
  });

  const handleClear = useEvent((): void => {
    if (!lastValue && fieldName) {
      setLastValue(get(rowOriginal, fieldName));
    }
    updateValue(null);
  });

  return isFieldVisible ? (
    <RideEditableDropdownBoundTemplateStyled
      className="cell_simple"
      data-e2e-custom={e2eData}
      data-e2e-type="dropdown-bound-cell-template"
    >
      {!isDisabledByCondition && (
        <IconButton
          onClick={handleClick}
          size="small"
          disabled={isFieldDisabled}
        >
          <EditIcon className="edit-icon" />
        </IconButton>
      )}
      {phoneCallable && (
        <FramerTooltip
          content={formatPhoneLocal(phoneCallable)}
          placement={PopoverPlacement.bottom}
        >
          <div className="phone_forwarded_icon">
            <PhoneForwardedIcon
              onClick={() => callToClickHandler(phoneCallable)}
            />
          </div>
        </FramerTooltip>
      )}
      <FramerTooltip content={valueComputed} rtlStrippedBottomLeftPlacement>
        {!isNullOrEmptySpace(valueComputed) ? valueComputed : NO_VALUE_DISPLAY}
      </FramerTooltip>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: isRtl ? "right" : "left",
        }}
      >
        <RideEditableDropdownPopoverContainer>
          <Select
            // eslint-disable-next-line jsx-a11y-x/no-autofocus -- intentional: focus the field when opened by user action
            autoFocus
            classNames={{
              control: state =>
                state.isFocused ? "react-select__control--focused" : "",
              menu: () => "react-select__menu",
              menuList: () => "react-select__menu-list",
              option: getOptionClassName,
            }}
            styles={{
              option: (provided, state) => ({
                ...provided,
                fontWeight: state.data.value === lastValue ? "bold" : "normal",
              }),
            }}
            backspaceRemovesValue={false}
            data-e2e="editable-dropdown-bound"
            components={{
              DropdownIndicator: null,
              IndicatorSeparator: null,
            }}
            controlShouldRenderValue={false}
            isClearable
            menuIsOpen
            options={sortedDropdownItems}
            onChange={handleOnChange}
            tabSelectsValue={false}
          />
        </RideEditableDropdownPopoverContainer>
        <RideEditableDropdownPopoverFooter>
          <button className="clear-button" onClick={handleClear}>
            <CloseIcon className="clear-icon" />
            <span className="clear-text">{t("general.CLEAR")}</span>
          </button>
        </RideEditableDropdownPopoverFooter>
      </Popover>
    </RideEditableDropdownBoundTemplateStyled>
  ) : null;
};
